/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3a §6] THE WORDS A DUTY WITH NO FIXED TIME IS LABELLED WITH.
 *
 *  ⚠️ IT IS IN `constants/` AND NOT `utils/`, AND THAT IS AN ESLINT FACT RATHER THAN A
 *  PREFERENCE. `pwa/eslint.config.js` bans `@pams/shared/utils` at runtime — that barrel drags
 *  zod and luxon, which is a quarter of Duty Mitra's 150 KB budget — with the message "Move what
 *  you need to constants/". Duty Mitra renders the stored `slot_label` on its duty section, so it
 *  is one of the three readers and the literal has to live where it can reach.
 *
 *  ⚠️ ONE EXPRESSION, THREE READERS. `generateSlots` WRITES it into `deployments.slot_label` at
 *  publish; `withReportingTime` (in `utils/timeFormat.util.ts`) rewrites it at the render site for
 *  `web` and the API; and `pwa` does its own replace against it. Before this pass the same six
 *  characters were typed in two places and nothing held them together.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * The tail `generateSlots` stamps onto a whole-day duty's stored label — as in
 * `Sat 15 Aug · No fixed time`.
 *
 * ⚠️ IT IS THE STORED FORM AND IT DOES NOT MOVE. Every order already issued carries it, and §6 is
 * a LABELLING rule with no migration — so the substitution happens where the label is READ, which
 * is `humaniseTimes`' own precedent one function over.
 */
export const NO_FIXED_TIME_LABEL = 'No fixed time';

/**
 * What a duty with no shift but a reporting time reads as: `From 3:00 PM`.
 *
 * ⚠️ IT TAKES AN ALREADY-FORMATTED TIME, so it needs no formatter and no zone. `web` and the API
 * hand it `toTwelveHour(...)` (`3:00 PM`); Duty Mitra builds the same sentence from its own
 * catalogue and its own `Intl` formatter (`3:00 pm`), because that package cannot import luxon and
 * its lowercase house style predates this pass. Sharing the FORMAT would have meant sharing a
 * formatter, which is the one thing `pwa/src/lib/format.ts` exists not to do.
 *
 * ⚠️ AND IT IS OPEN-ENDED ON PURPOSE. `From 3:00 PM`, never `3:00 PM – ?`: the duty genuinely has
 * no end time, `hasFixedTime` stays FALSE, and a dash with nothing after it reads as a shift whose
 * second half somebody forgot to fill in.
 */
/**
 * What `slotTimeLabel` says for a whole-day duty that has NO reporting time — the display twin of
 * `NO_FIXED_TIME_LABEL`.
 *
 * ⚠️ THE TWO ARE DIFFERENT STRINGS ON PURPOSE AND ARE NOT UNIFIED HERE. `NO_FIXED_TIME_LABEL` is
 * the STORED tail of a duty's own label (`Sat 15 Aug · No fixed time`); this is a FRESH string
 * built for a cell that already has the date in a column beside it. [C3a §6] is a substitution,
 * not a copy change — unifying the product's three phrasings (`Whole day`, `Whole Day`,
 * `No fixed time`) moves six literals and fourteen assertions for no functional gain, and is
 * recorded as a known divergence rather than done in passing.
 */
export const WHOLE_DAY_LABEL = 'Whole day';

export const reportingFromLabel = (formattedTime: string): string => `From ${formattedTime}`;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [STAND-DOWN] A WHOLE-DAY DUTY MAY ALSO STATE WHEN IT STANDS DOWN.
 *
 *  The reporting time already leads a whole-day duty (`From 3:00 PM`). An optional stand-down
 *  time is the matching END, so with both the duty reads like a shift — `3:00 PM–9:00 PM` — and
 *  with the stand-down alone it reads `Until 9:00 PM`.
 *
 *  ⚠️ IT LIVES HERE, WITH NO FORMATTER, FOR `reportingFromLabel`'s REASON: Duty Mitra cannot
 *  import `utils/` and formats with its own `Intl` output, so the SENTENCE is shared and the
 *  formatting is not.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const standDownUntilLabel = (formattedTime: string): string => `Until ${formattedTime}`;

/**
 * ⚠️ A STAND-DOWN AT OR BEFORE THE REPORTING TIME IS THE NEXT MORNING — `20:00` then `02:00`.
 *
 * The same `<=` rule `generateSlots` applies to a shift's end, and sound for the same reason:
 * both are zero-padded `HH:mm`, so a string comparison is chronological.
 *
 * ⚠️ WITH NO REPORTING TIME THE DUTY STARTS AT `00:00` OF ITS DATE, AND THAT IS THE COMPARAND — not
 * "nothing rolls over". Only a `00:00` stand-down is then at-or-before the start, and it means the
 * END of the day; treating it as same-day stamped `stand_down_at` equal to `starts_at`, a zero-length
 * window reading `Until 12:00 AM`. `timeOfDaySchema` refuses `24:00`, so `00:00` is the only way an
 * operator can say "until midnight".
 */
export const standDownRollsOver = (reportingTime: string | null, standDownTime: string): boolean =>
  standDownTime <= (reportingTime ?? '00:00');

/** Already-formatted times in, the whole-day time fragment out — or `null` when neither is set. */
export const wholeDayWindowLabel = (from: string | null, until: string | null, nextDay: boolean): string | null => {
  if (from !== null && until !== null) return `${from}–${until}${nextDay ? ' (+1 day)' : ''}`;
  if (from !== null) return reportingFromLabel(from);
  /* ⚠️ The suffix applies here too — `Until 12:00 AM (+1 day)` is the midnight case above. */
  if (until !== null) return `${standDownUntilLabel(until)}${nextDay ? ' (+1 day)' : ''}`;
  return null;
};
